import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { getThreatHuntingAlerts as getThreatHuntingAlertsService } from 'services/modules/soc/agent';

import { Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, TablePagination, Tooltip, Button, MenuItem, FormControl, Select, InputLabel } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';  
import Breadcrumbs from 'config/Breadcrumb';

const Dashboard = () => {
  // React router
  const location = useLocation();
  // use navigate hook
  const navigate = useNavigate()
  // Redux dispatch
  const dispatch = useDispatch()
  
  // Internal state and query params
  const endDate = location.state && location.state.end;
  const startDate = location.state && location.state.start;
  const queryParams = new URLSearchParams(location.search);
  const agentId = queryParams.get('agentid');
  const alertSeverity = queryParams.get('severity');
  
  // State Variables
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentPath, setCurrentPath] = useState(['Security Alerts']);
  const severityOptions = ["all", "low", "medium", "high", "critical"];
  const [severity, setSeverity] = useState("");

  const selectedCompanyContainer = useSelector((state) => state.auth.companyData)
  const responseContainer = useSelector(state => state.socData.securityAlerts)
  
  // Function to get severity label based on rule level
  const getSeverityLabel = useCallback((ruleLevel) => {
    if (ruleLevel >= 0 && ruleLevel <= 6) {
      return "Low";
    } else if (ruleLevel >= 7 && ruleLevel <= 8) {
      return "Medium";
    } else if (ruleLevel >= 9 && ruleLevel <= 11) {
      return "High";
    } else if (ruleLevel >= 12) {
      return "Critical";
    }
    return "";
  }, []);
  
  // use effect sets the severity to selected severity by user
  useEffect(() => {
    if (alertSeverity) {
      setSeverity(alertSeverity)
    }
    else
      setSeverity("all")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  
  // Fetches Threat Hunting alerts based on agent id
  useEffect(() => {
    const fetchData = () => {
      if (severity !== "") {
        try {
          setLoading(true);
          const params = { agentId: agentId, pagestart: page * rowsPerPage, limit: rowsPerPage, severity: severity, soc_env: selectedCompanyContainer?.soc_env, end: endDate, start: startDate }
          dispatch(getThreatHuntingAlertsService({ params }));
        } catch (error) {
        }
      }
    };

    if (agentId) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, page, rowsPerPage, severity, endDate, startDate]);

  useEffect(() => {
    if (responseContainer && responseContainer.data) {
      const groupedData = responseContainer.data.map(hit => {
        const utcDate = new Date(hit._source.timestamp);
        const isUTC = utcDate.getUTCHours() === utcDate.getHours();
        const istDate = isUTC ? new Date(utcDate.getTime() + 5.5 * 60 * 60 * 1000) : utcDate;


        return {
          timestamp: istDate.toLocaleString('en-GB', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }).replace(',', '').replace(' at', ' @'),

          agentName: hit._source.agent.name,
          ruleName: hit._source.rule.id,
          ruleDescription: hit._source.rule.description,
          ruleLevel: hit._source.rule.level,
          alertId: hit._id,
          agentip: hit._source.agent.ip
        };
      });

      setData(groupedData);

      // Ensure safe access to agentName
      setCurrentPath(['Dashboard', 'Threat Hunting', groupedData[0]?.agentName || 'Unknown']);

      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseContainer]);


  // Handles change in page
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  // Handles change in rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };
  
  // Navigates to single alert page
  const viewAlerts = (alert) => {
    let severity = getSeverityLabel(alert?.ruleLevel);
    navigate('/soc/view-security-alert', { state: { alertId: alert.alertId, name: 'securityAlert', timestamp: alert.timestamp, severity: severity } });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <Breadcrumbs paths={currentPath} />
      <div>
        <Typography variant="h5" align="center">View Alerts</Typography>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div className=" col-lg-2 col-md-4 col-sm-12 col-xs-12 search-ecs">
          <FormControl fullWidth size='small'>
            <InputLabel>Severity</InputLabel>
            <Select
              value={severity}
              label="Severity"
              onChange={(event) => { setSeverity(event.target.value); setPage(0) }}
            >
              {severityOptions.map(severity =>
                <MenuItem value={severity}>{severity.substring(0, 1).toUpperCase() + severity.slice(1)}</MenuItem>
              )}
            </Select>
          </FormControl>
        </div>
        <div>
          <Button
            variant="contained"
            className="back font"
            style={{ position: 'relative', bottom: '5px' }}
            onClick={() => window.history.back()}
          >
            Back
          </Button>
        </div>
      </div>
      <p className='my-2 mx-2'><strong>Alerts Range:</strong><span className='text-primary'>{startDate.toLocaleString()} - {new Date(endDate).toLocaleString()}</span> </p>
      <TableContainer className="mx-2">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align='center'>Incident Time</TableCell>
              <TableCell align='center'>Agent Name</TableCell>
              <TableCell align='center'>Agent IP</TableCell>
              <TableCell style={{ whiteSpace: 'wrap' }}>Description</TableCell>
              <TableCell align='center'>Severity</TableCell>
              <TableCell align='center'>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <WarningIcon color="disabled" fontSize="small" />
                  No Data Available
                </TableCell>
              </TableRow>
            ) : (
              data.map((alert, index) => (
                <TableRow key={index}>
                  <TableCell align='center' className="text-primary"><b>{alert.timestamp}</b></TableCell>
                  <TableCell align='center'>{alert.agentName}</TableCell>
                  <TableCell align='center' className='text-secondary'><b>{alert.agentip}</b></TableCell>
                  <TableCell className='wrap'>{alert.ruleDescription && alert.ruleDescription.length > 70 ? alert.ruleDescription.slice(0, 70) + '...' : alert.ruleDescription}</TableCell>
                  <TableCell align='center'>
                    <div className="rating-value">
                      <span
                        className={`${getSeverityLabel(alert.ruleLevel)
                            ? getSeverityLabel(alert.ruleLevel).toLowerCase()
                            : ""
                          }`}
                        style={{
                          width: getSeverityLabel(alert.ruleLevel) ? '70px' : 'auto',
                        }}
                      >
                        {getSeverityLabel(alert.ruleLevel)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell align='center'>
                    <Tooltip title="View" placement="top">
                      <img
                        src="/assets/viewicon.svg"
                        alt="View"
                        className='icons-style'
                        onClick={() => viewAlerts(alert)}
                      />
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[20, 25, 50, 100, 250, 500]}
        component="div"
        count={responseContainer?.totalRecords||0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </div>
  );
};

export default Dashboard;
